import { useState } from "react";

// ────────────────────────────────────────────────────────────────────────
// DBLearn — Privacy Policy
// Same visual system/pattern as the PDF Studio policy page (dark "legal
// document" layout, Space Grotesk display type), palette pulled from
// DBLearn's own in-app theme (terminal green accent, near-black bg) and
// content written for what DBLearn actually does: local SQLite progress
// storage, no account, no user file uploads, and Google Mobile Ads
// (AdMob) for monetization with GDPR/UMP consent.
//
// BEFORE DEPLOYING:
//   1. Confirm CONTACT_EMAIL / DEVELOPER_NAME are correct.
//   2. Re-read every section against what the app actually does. If you
//      add analytics, a backend, or cloud sync later, this page must be
//      updated to stay accurate — a privacy policy that doesn't match
//      real behavior is a compliance risk, not just a copy problem.
//   3. If you ever mark the app as "Designed for Families" / child-
//      directed in Play Console, the "Children's privacy" and "Ads"
//      sections below need to be rewritten — child-directed apps have
//      much stricter AdMob requirements than what's described here.
// ────────────────────────────────────────────────────────────────────────

const CONTACT_EMAIL = "alphay28071994@gmail.com"; // TODO: replace with your real support email
const DEVELOPER_NAME = "AlphaY"; // TODO: replace with your name/company

// Pulled from DBLearn's in-app theme (lib/theme/app_theme.dart) so the
// policy page reads as part of the same product, not a generic template.
const palette = {
    bg: "#0F1117",
    surface: "#181C27",
    raised: "#1E2335",
    border: "#252A3A",
    accent: "#5DCAA5",
    accentDim: "#0D2B20",
    text: "#E8EAF0",
    muted: "#9CA3AF",
    faint: "#4B5563",
    success: "#5DCAA5",
    successDim: "#0D2B20",
    danger: "#E24B4A",
    dangerDim: "#2B0F0F",
    warning: "#EF9F27",
    warningDim: "#2B1F0A",
    info: "#5B9BD5",
    infoDim: "#0D1E33",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500&family=JetBrains+Mono:wght@500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }

  .db-pp {
    background: ${palette.bg};
    color: ${palette.text};
    font-family: 'Inter', sans-serif;
    font-size: 15px;
    line-height: 1.7;
    min-height: 100vh;
    position: relative;
    overflow-x: hidden;
  }

  .db-bars-bg {
    position: fixed;
    inset: 0;
    background-image: repeating-linear-gradient(
      90deg,
      ${palette.border}22 0px,
      ${palette.border}22 2px,
      transparent 2px,
      transparent 60px
    );
    pointer-events: none;
    z-index: 0;
    opacity: 0.5;
  }

  .db-glow {
    position: fixed;
    top: -100px;
    left: 50%;
    transform: translateX(-50%);
    width: 600px;
    height: 400px;
    background: radial-gradient(ellipse, ${palette.accent}18 0%, transparent 65%);
    pointer-events: none;
    z-index: 0;
    animation: db-pulse 5s ease-in-out infinite alternate;
  }

  @keyframes db-pulse {
    from { opacity: 0.6; }
    to   { opacity: 1; }
  }

  .db-wrap {
    position: relative;
    z-index: 1;
    max-width: 760px;
    margin: 0 auto;
    padding: 0 20px 80px;
  }

  .db-hero {
    text-align: center;
    padding: 56px 0 44px;
    animation: db-fadeup .6s ease both;
  }

  .db-mark {
    width: 48px;
    height: 48px;
    margin: 0 auto 18px;
    border-radius: 12px;
    background: ${palette.accentDim};
    border: 1px solid ${palette.accent}55;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'JetBrains Mono', monospace;
    font-weight: 700;
    font-size: 15px;
    letter-spacing: 0.5px;
    color: ${palette.accent};
  }

  .db-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: ${palette.accentDim};
    border: 1px solid ${palette.accent}44;
    border-radius: 100px;
    padding: 5px 14px;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.5px;
    color: ${palette.accent};
    text-transform: uppercase;
    margin-bottom: 20px;
  }

  .db-hero h1 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: clamp(30px, 6vw, 48px);
    font-weight: 700;
    letter-spacing: -1px;
    line-height: 1.1;
    margin-bottom: 12px;
  }

  .db-hero h1 em { color: ${palette.accent}; font-style: normal; }

  .db-hero-sub {
    color: ${palette.muted};
    font-size: 14px;
    max-width: 440px;
    margin: 0 auto 18px;
  }

  .db-meta {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px 20px;
    color: ${palette.faint};
    font-size: 12px;
    font-family: 'JetBrains Mono', monospace;
  }

  .db-meta span {
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .db-divider {
    height: 1px;
    background: linear-gradient(90deg, transparent, ${palette.border}, transparent);
    margin: 36px 0;
  }

  .db-section {
    background: ${palette.surface};
    border: 1px solid ${palette.border};
    border-radius: 16px;
    padding: 24px 26px 20px;
    margin-bottom: 12px;
    transition: border-color .2s;
    animation: db-fadeup .5s ease both;
  }

  .db-section:hover { border-color: ${palette.accent}30; }

  .db-section-head {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
  }

  .db-icon {
    width: 36px; height: 36px; min-width: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
  }

  .db-section h2 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -.1px;
  }

  .db-section p, .db-section li {
    color: ${palette.muted};
    font-size: 14px;
    line-height: 1.75;
  }

  .db-section ul {
    padding-left: 18px;
    margin-top: 8px;
  }

  .db-section li { margin-bottom: 5px; }

  .db-section p + p { margin-top: 10px; }

  .db-section a {
    color: ${palette.info};
    text-decoration: none;
    border-bottom: 1px solid ${palette.info}44;
    transition: border-color .15s;
  }

  .db-section a:hover { border-color: ${palette.info}; }

  .db-box {
    border-radius: 10px;
    padding: 11px 15px;
    margin-top: 14px;
    font-size: 13px;
    display: flex;
    gap: 9px;
    align-items: flex-start;
    line-height: 1.6;
  }

  .db-box-success {
    background: ${palette.successDim};
    border: 1px solid ${palette.success}33;
    color: ${palette.success}CC;
  }

  .db-box-warning {
    background: ${palette.warningDim};
    border: 1px solid ${palette.warning}33;
    color: ${palette.warning}CC;
  }

  .db-box-danger {
    background: ${palette.dangerDim};
    border: 1px solid ${palette.danger}33;
    color: ${palette.danger}CC;
  }

  .db-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin: 14px 0 4px;
  }

  @media (max-width: 520px) { .db-grid { grid-template-columns: 1fr; } }

  .db-card {
    background: ${palette.raised};
    border: 1px solid ${palette.border};
    border-radius: 12px;
    padding: 13px 15px;
    display: flex;
    align-items: flex-start;
    gap: 11px;
  }

  .db-card-icon {
    width: 32px; height: 32px; min-width: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
  }

  .db-card strong {
    font-size: 13px;
    font-weight: 600;
    color: ${palette.text};
    display: block;
    margin-bottom: 3px;
  }

  .db-card p {
    font-size: 12px;
    color: ${palette.muted};
    line-height: 1.5;
    margin: 0 !important;
  }

  .db-contact {
    background: ${palette.accentDim};
    border: 1px solid ${palette.accent}33;
    border-radius: 16px;
    padding: 32px;
    text-align: center;
    margin-top: 36px;
  }

  .db-contact h3 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 20px;
    font-weight: 700;
    margin-bottom: 8px;
  }

  .db-contact p {
    color: ${palette.muted};
    font-size: 14px;
    margin-bottom: 20px;
  }

  .db-contact-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: ${palette.accent};
    color: #0F1117;
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    font-size: 14px;
    padding: 12px 24px;
    border-radius: 100px;
    text-decoration: none;
    transition: opacity .2s, transform .2s;
  }

  .db-contact-btn:hover { opacity: .87; transform: translateY(-1px); }

  .db-footer {
    text-align: center;
    padding-top: 28px;
    color: ${palette.faint};
    font-size: 12px;
  }

  @keyframes db-fadeup {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

const Ico = ({ e }) => <span role="img" aria-hidden="true">{e}</span>;

function SecIcon({ bg, children }) {
    return <div className="db-icon" style={{ background: bg }}>{children}</div>;
}

function Section({ iconBg, icon, title, children, delay = 0 }) {
    return (
        <div className="db-section" style={{ animationDelay: `${delay}s` }}>
            <div className="db-section-head">
                <SecIcon bg={iconBg}><Ico e={icon} /></SecIcon>
                <h2>{title}</h2>
            </div>
            {children}
        </div>
    );
}

function SuccessBox({ children }) {
    return <div className="db-box db-box-success"><Ico e="✓" /><span>{children}</span></div>;
}

function WarnBox({ children }) {
    return <div className="db-box db-box-warning"><Ico e="⚠" /><span>{children}</span></div>;
}

function InfoCard({ icon, iconBg, title, desc }) {
    return (
        <div className="db-card">
            <div className="db-card-icon" style={{ background: iconBg }}>
                <Ico e={icon} />
            </div>
            <div>
                <strong>{title}</strong>
                <p>{desc}</p>
            </div>
        </div>
    );
}

export default function DBLearnPrivacyPolicy() {
    const [year] = useState(new Date().getFullYear());

    return (
        <>
            <style>{css}</style>
            <div className="db-pp">
                <div className="db-bars-bg" />
                <div className="db-glow" />

                <div className="db-wrap">

                    <header className="db-hero">
                        <div className="db-mark">DB</div>
                        <div className="db-badge">🔐 &nbsp;Legal document</div>
                        <h1>Privacy <em>Policy</em></h1>
                        <p className="db-hero-sub">
                            How <strong>DBLearn</strong> collects, uses, and protects your information.
                        </p>
                        <div className="db-meta">
                            <span>🏢 Developer: {DEVELOPER_NAME}</span>
                            <span>📅 Effective: January {year}</span>
                            <span>📱 No account · No file uploads</span>
                        </div>
                    </header>

                    <div className="db-divider" />

                    <Section iconBg={palette.accentDim} icon="🗄️" title="Overview" delay={0.05}>
                        <p>
                            <strong style={{ color: palette.text }}>DBLearn</strong> is a database and SQL
                            learning app: interactive lessons, quizzes, coding challenges, and a SQL
                            playground that runs entirely against sample and user-created databases stored
                            on your own device. This page explains what information (if any) is collected
                            and how it's used.
                        </p>
                        <p>By using DBLearn, you agree to the practices described below.</p>
                        <SuccessBox>
                            DBLearn requires no account or sign-in, and does not collect your name, email,
                            or any other personal identifying information.
                        </SuccessBox>
                    </Section>

                    <Section iconBg={palette.infoDim} icon="🗂️" title="Information we collect" delay={0.10}>
                        <p>
                            <strong style={{ color: palette.text }}>We do not operate any servers that this app sends your learning data to.</strong>{" "}
                            There is no account system and no crash-reporting or analytics service built by
                            us into this app.
                        </p>
                        <p>
                            Everything DBLearn tracks about your use of the app — completed lessons, quiz
                            scores, solved challenges, saved SQL snippets, query history, and any custom
                            playground databases you create — is stored in a SQLite database inside the
                            app's private storage area on your device. None of it leaves your device or is
                            visible to us.
                        </p>
                        <SuccessBox>
                            Uninstalling the app, or clearing its storage in your device settings,
                            permanently deletes all of this data.
                        </SuccessBox>
                    </Section>

                    <Section iconBg={palette.warningDim} icon="📢" title="Advertising" delay={0.15}>
                        <p>
                            DBLearn is free to use and supported by ads served through{" "}
                            <strong style={{ color: palette.text }}>Google Mobile Ads (AdMob)</strong>,
                            shown as banners, native placements, and occasional interstitials between
                            sections of the app.
                        </p>
                        <p>
                            To serve ads, Google's SDK may collect device and advertising identifiers (such
                            as your advertising ID), approximate location derived from IP address, and
                            other technical data, and may use these to personalize the ads you see. This
                            processing is performed by Google, not by DBLearn directly — we don't receive
                            or store this data ourselves.
                        </p>
                        <p>
                            Where required (for example, in the EEA, UK, and Switzerland), DBLearn shows a
                            consent form — built on Google's User Messaging Platform (UMP) — before any
                            personalized ads are requested, so you can choose how your data is used for
                            advertising, or opt out of personalization entirely.
                        </p>
                        <WarnBox>
                            Google's ad and consent handling is governed by Google's own privacy policy —{" "}
                            <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
                                policies.google.com/privacy
                            </a>{" "}
                            and{" "}
                            <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noreferrer">
                                policies.google.com/technologies/ads
                            </a>
                        </WarnBox>
                    </Section>

                    <Section iconBg={palette.successDim} icon="🔑" title="Permissions used" delay={0.20}>
                        <p>
                            DBLearn requests network access, used only to request and display ads and (on
                            first run) to fetch app fonts. It does not request camera, microphone,
                            contacts, SMS, or location permissions, and does not read or write files
                            outside of its own private storage.
                        </p>
                    </Section>

                    <Section iconBg="#0A1525" icon="🔌" title="Third-party components" delay={0.25}>
                        <p>DBLearn is built using the following components:</p>
                        <div className="db-grid">
                            <InfoCard
                                icon="📢" iconBg={palette.warningDim}
                                title="Google Mobile Ads (AdMob)"
                                desc="Serves in-app ads; see Advertising above."
                            />
                            <InfoCard
                                icon="✅" iconBg={palette.infoDim}
                                title="Google User Messaging Platform"
                                desc="Collects your ad-consent choice where required."
                            />
                            <InfoCard
                                icon="🔤" iconBg={palette.accentDim}
                                title="Google Fonts"
                                desc="Loads app typefaces; no personal data involved."
                            />
                            <InfoCard
                                icon="🗄️" iconBg={palette.successDim}
                                title="SQLite (sqflite)"
                                desc="Stores progress and playground data on-device."
                            />
                            <InfoCard
                                icon="⚙️" iconBg="#1A100A"
                                title="Local app preferences"
                                desc="Stores lightweight app settings on-device."
                            />
                            <InfoCard
                                icon="🖥️" iconBg="#0A1A10"
                                title="Local SQL playground"
                                desc="Your queries run against on-device sample data only."
                            />
                        </div>
                    </Section>

                    <Section iconBg="#0A1525" icon="🕓" title="Data retention" delay={0.30}>
                        <p>
                            Your progress, scores, snippets, and playground databases remain in the app's
                            local storage until you clear them yourself, reset a database in the playground,
                            or uninstall the app — at which point the operating system removes them
                            automatically.
                        </p>
                        <p>We do not maintain any server-side copy of this data.</p>
                    </Section>

                    <Section iconBg={palette.dangerDim} icon="🧒" title="Children's privacy" delay={0.35}>
                        <p>
                            DBLearn is a general-audience educational tool and is not directed at children
                            under 13. We do not knowingly collect personal information from children. Ads
                            shown in the app are served under Google's standard (non-child-directed)
                            treatment.
                        </p>
                        <p>
                            If you believe a child has used the app in a way that concerns you, please
                            contact us at{" "}
                            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
                        </p>
                    </Section>

                    <Section iconBg={palette.successDim} icon="🛡️" title="Security" delay={0.40}>
                        <p>
                            Your learning data and playground databases live in DBLearn's private,
                            sandboxed storage on your device, inaccessible to other apps.
                        </p>
                        <p>
                            Since nothing is transmitted to us, the biggest safeguard is your own device
                            security — keep it locked with a passcode or biometric lock, and keep the app
                            updated to receive any security fixes.
                        </p>
                    </Section>

                    <Section iconBg={palette.warningDim} icon="🔄" title="Changes to this policy" delay={0.45}>
                        <p>
                            We may update this privacy policy from time to time. Changes will be posted on
                            this page with an updated effective date at the top.
                        </p>
                        <p>
                            Continued use of DBLearn after changes are posted constitutes acceptance of the
                            updated policy.
                        </p>
                    </Section>

                    <Section iconBg={palette.successDim} icon="✅" title="Your consent" delay={0.50}>
                        <p>
                            By using DBLearn, you consent to this privacy policy. If you do not agree,
                            please discontinue use of the app.
                        </p>
                    </Section>

                    <div className="db-contact">
                        <h3>Questions or concerns?</h3>
                        <p>
                            If you have any questions about how DBLearn handles your data, don't hesitate
                            to get in touch.
                        </p>
                        <a href={`mailto:${CONTACT_EMAIL}`} className="db-contact-btn">
                            ✉️ &nbsp;{CONTACT_EMAIL}
                        </a>
                    </div>

                    <div className="db-divider" />
                    <footer className="db-footer">
                        <p>© {year} <strong style={{ color: palette.muted }}>{DEVELOPER_NAME}</strong> · DBLearn · All rights reserved.</p>
                        <p style={{ marginTop: 5 }}>This app is not affiliated with or endorsed by Google LLC.</p>
                    </footer>

                </div>
            </div>
        </>
    );
}